import RevisionModal from "components/modals/RevisionModal";
import PageHeading from "components/shared/PageHeading";
import React, { useEffect, useState } from "react";
import { Col, Row, Form, Button, Placeholder, Alert, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  getSubmission,
  markSubmission,
  sendToRevision,
} from "services/Student/Submission.services";
import { useAuth } from "utils/AuthContext";
import { formatText } from "utils/FormatText";
import useAutoSave from "hooks/useAutoSave";
import AutoSaveIndicator from "components/shared/AutoSaveIndicator";
import { getFormDraft, patchFormDraft, deleteFormDraft } from "services/formDraft.service";

const MarkSubmission = () => {
  const { userRoles } = useAuth();
  const { t } = useTranslation();
  const [exerciseInfo, setExerciseInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submittedAnswers, setSubmittedAnswers] = useState({});
  const [marks, setMarks] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [totalScore, setTotalScore] = useState(0);
  const [maxMarks, setMaxMarks] = useState(0);
  const [isMarked, setIsMarked] = useState(false);
  const [status, setStatus] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [audioUrl, setAudioUrl] = useState("");
  const [hydrated, setHydrated] = useState(false);
  // Modal used to capture the teacher's correct answer when they override the
  // auto-marking of a multiple-choice / true-false question.
  const [overrideModal, setOverrideModal] = useState({
    show: false,
    questionId: null,
    score: 0,
    value: "",
  });

  const navigate = useNavigate();
  const params = useParams();
  const id = params._id;

  // Review feedback is auto-saved to a per-submission server-side draft rather
  // than being finalized, because the server save (markSubmission) finalizes
  // grading. This keeps a teacher's in-progress comments safe without
  // prematurely marking.
  const getDraftKey = () => `submission-review-draft-${id}`;

  const clearDraft = async () => {
    await deleteFormDraft(getDraftKey()).catch(() => {});
  };

  // Auto-save review work. Feedback text debounces ~2.5s; per-question score/
  // feedback edits flush immediately via saveImmediately() so every review
  // action persists instantly. Saved to a per-submission server-side draft
  // (the server save finalizes grading, so it must stay an explicit action) —
  // only the changed fields are PATCHed to the server.
  const {
    status: autoSaveStatus,
    lastSavedAt: autoSaveAt,
    isOnline: autoSaveOnline,
    saveImmediately: saveReviewImmediately,
    markSaved: markReviewSaved,
  } = useAutoSave(
    { marks, feedback },
    (changed) => patchFormDraft(getDraftKey(), changed),
    {
      // hydrated stays false until BOTH the server submission load and the
      // draft restore have been applied, so autosave can't overwrite mid-hydration.
      enabled: Boolean(id) && !isMarked && hydrated,
      canSave: () => marks.length > 0,
    }
  );

  useEffect(() => {
    let cancelled = false;
    // Re-gate autosave whenever the submission id changes, until both the
    // server load and draft restore below have re-applied.
    setHydrated(false);
    const fetchSubmissionDetails = async () => {
      try {
        setLoading(true);
        const response = await getSubmission(id);
        const { exercise, answers, status, totalScore } =
          response?.data?.result;

        setIsMarked(status === "marked");
        setStatus(status);
        setTotalScore(totalScore);
        setAudioUrl(exercise?.audioUrl?.url);


        const totalPossibleMarks = exercise.questions.reduce(
          (sum, question) => sum + question.score,
          0
        );

        setMaxMarks(totalPossibleMarks);

        const answersMap = {};
        const initialMarks = [];
        answers.forEach((ans) => {
          answersMap[ans.question] = ans.answer;
          const question = exercise.questions.find(
            (q) => q._id === ans.question
          );
          if (!question) return;

          if (
            ["written-answer", "essay", "fill-in-the-blank"].includes(
              question.type
            )
          ) {
            initialMarks.push({
              questionId: ans.question,
              score: ans.score || 0,
              feedback: ans.feedback || "",
            });
          } else if (["multiple-choice", "true-false"].includes(question.type)) {
            // Seed the mark with the existing saved score when already marked,
            // otherwise with the auto-computed score. Keeping these in `marks`
            // lets a teacher override the AI/auto-marking (mark correct or
            // incorrect) before submitting.
            const autoScore = isCorrect(ans.answer, question.correctAnswer)
              ? question.score
              : 0;
            initialMarks.push({
              questionId: ans.question,
              score: status === "marked" ? ans.score || 0 : autoScore,
              feedback: ans.feedback || "",
              overridden: ans.overridden || false,
              correctAnswer: ans.overriddenCorrectAnswer || "",
            });
          }
        });

        setSubmittedAnswers(answersMap);

        // Restore any unsaved review draft (only for not-yet-marked submissions)
        // so a teacher who navigated away doesn't lose typed feedback/scores.
        // The draft (in-progress edits) must WIN over the pristine server marks:
        // we await the draft fetch here, before deciding whether to apply the
        // server-derived initialMarks, so the load effect can never clobber a
        // restored draft.
        let restoredMarks = initialMarks;
        let restoredFeedback = "";
        if (status !== "marked") {
          try {
            const res = await getFormDraft(getDraftKey());
            const draft = res?.data?.result;
            if (!cancelled && draft && Object.keys(draft).length > 0) {
              // Guard on Array (not truthiness of scores) so a draft whose
              // marks legitimately contain score 0 is still restored.
              if (Array.isArray(draft.marks) && draft.marks.length > 0) {
                restoredMarks = draft.marks;
              }
              // typeof-string guard so an intentionally-empty feedback (the
              // teacher cleared it) still restores rather than being dropped.
              if (typeof draft.feedback === "string") {
                restoredFeedback = draft.feedback;
              }
            }
          } catch (draftError) {
            console.error("Error restoring review draft:", draftError);
          }
        }
        if (cancelled) return;
        // Single source of truth for what lands in state: draft marks if a draft
        // exists, otherwise the pristine server marks. Never both.
        setMarks(restoredMarks);
        setFeedback(restoredFeedback);
        // Seed the auto-save baseline with exactly what we just loaded so an
        // untouched form is not immediately re-persisted as a draft (pristine
        // server marks) and a just-restored draft is not needlessly re-saved.
        markReviewSaved({ marks: restoredMarks, feedback: restoredFeedback });

        setExerciseInfo(exercise);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching submission details:", error);
        setErrorMessage(t("Failed to fetch submission details."));
        setLoading(false);
      } finally {
        // Enable autosave only after both the server marks and the draft have
        // been applied (or confirmed empty), preventing the mount-race overwrite.
        if (!cancelled) setHydrated(true);
      }
    };

    fetchSubmissionDetails();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const isCorrect = (studentAnswer, correctAnswer) => {
    return studentAnswer?.toLowerCase() === correctAnswer?.toLowerCase();
  };

  const handleMarkChange = (questionId, score, feedback) => {
    setMarks((prevMarks) =>
      prevMarks.map((mark) =>
        mark.questionId === questionId ? { ...mark, score, feedback } : mark
      )
    );
  };

  // Teacher marks a multiple-choice / true-false question as correct or
  // incorrect. If the choice matches the auto/AI result it is simply applied.
  // If it differs (an override), we open a modal so the teacher can supply the
  // correct answer that should be shown in place of the (possibly wrong) one.
  const handleMcqMark = (question, makeCorrect) => {
    const studentAnswer = submittedAnswers[question._id];
    const autoCorrect = isCorrect(studentAnswer, question.correctAnswer);
    const newScore = makeCorrect ? question.score : 0;

    if (makeCorrect === autoCorrect) {
      // Re-affirming the auto result — not an override, no modal needed.
      const nextMarks = marks.map((mark) =>
        mark.questionId === question._id
          ? { ...mark, score: newScore, overridden: false, correctAnswer: "" }
          : mark
      );
      setMarks(nextMarks);
      setErrorMessage(null);
      saveReviewImmediately({ marks: nextMarks }); // discrete action → immediate
      return;
    }

    // Overriding the auto result — ask the teacher for the correct answer.
    // Pre-fill with the student's answer when marking correct (their answer is
    // now the correct one), or the stored correct answer when marking incorrect.
    const prefill = makeCorrect
      ? studentAnswer || ""
      : question.correctAnswer || "";
    setOverrideModal({
      show: true,
      questionId: question._id,
      score: newScore,
      value: prefill,
    });
  };

  const handleConfirmOverride = () => {
    const { questionId, score, value } = overrideModal;
    const nextMarks = marks.map((mark) =>
      mark.questionId === questionId
        ? { ...mark, score, overridden: true, correctAnswer: value }
        : mark
    );
    setMarks(nextMarks);
    setOverrideModal({ show: false, questionId: null, score: 0, value: "" });
    setErrorMessage(null);
    saveReviewImmediately({ marks: nextMarks }); // discrete action → immediate
  };

  const handleCancelOverride = () => {
    setOverrideModal({ show: false, questionId: null, score: 0, value: "" });
  };

  const isWritingExercise = exerciseInfo?.exerciseType === "writing";
  const isReadingExercise = exerciseInfo?.exerciseType === "reading";
  const isListeningExercise = exerciseInfo?.exerciseType === "listening";

  const handleShowModal = () => {
    setShowModal(true);
  };

  const handleHideModal = () => {
    setShowModal(false);
  };


  useEffect(() => {
    const writtenQuestions = exerciseInfo?.questions.filter((q) =>
      ["written-answer", "essay", "fill-in-the-blank"].includes(q.type)
    );
    const allWrittenQuestionsMarked = writtenQuestions?.every((question) => {
      const mark = marks.find((mark) => mark.questionId === question._id);
      return (
        mark && mark.score !== null && mark.score !== "" && mark.score >= 0
      );
    });

    setIsFormValid(allWrittenQuestionsMarked);
  }, [marks, exerciseInfo]);

  const handleScoreChange = (questionId, score, maxScore) => {
    if (score > maxScore) {
      setErrorMessage(`${t('Score for this question cannot exceed')} ${maxScore}`);
      return;
    }

    setMarks((prevMarks) =>
      prevMarks.map((mark) =>
        mark.questionId === questionId
          ? { ...mark, score: score, feedback: mark.feedback }
          : mark
      )
    );
    setErrorMessage(null);
  };

  const handleSubmitMarks = async () => {
    if (!isFormValid) {
      setErrorMessage(t("Please mark all questions before submitting."));
      return;
    }

    try {
      const payload = {
        submissionId: id,
        marks,
      };

      await markSubmission(payload);
      await clearDraft();
      navigate(-1);
    } catch (error) {
      console.error("Error submitting marks:", error);
      setErrorMessage(t("Error submitting marks. Please try again."));
    }
  };

  const handleRevision = async () => {
    try {
      setLoading(true);
      const payload = {
        submissionId: id,
        feedback,
      };

      await sendToRevision(payload);
      await clearDraft();
      handleHideModal();
      setLoading(false);
      navigate(-1);
    } catch (error) {
      setLoading(false);
      console.error("Error submitting marks:", error);
      setErrorMessage(t("Error submitting marks. Please try again."));
    }
  };

  return (
    <>
      <div>
        {exerciseInfo ? (
          <div className="d-flex justify-content-between">
            <div>
              <PageHeading
                heading={exerciseInfo?.exerciseType}
                text={`Title: ${exerciseInfo?.title}`}
              />
            </div>
            <div>
              {isMarked ? (
                <h5 className="pt-2 mb-0 me-2"> {t("Total Score")}: {totalScore} / {maxMarks}</h5>
              ) : (
                <h5 className="pt-2 mb-0 me-2 text-capitalize">{t("Status")}: {status}</h5>

              )}
              {/* {!isMarked &&
                <h5 className="pt-2 mb-0 me-2 text-capitalize">Status: {status}</h5>
              }
              {isMarked &&
                <h5 className="pt-2 mb-0 me-2"> Total Score: {totalScore} / {maxMarks}</h5>
              } */}
              {/* : (
                )} */}
            </div>

          </div>
        ) : (
          <Placeholder className="mb-3" as="div" animation="glow">
            <Placeholder xs={3} className="mb-2" />
            <div>
              <Placeholder xs={6} />
            </div>
          </Placeholder>
        )}
      </div>

      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}



      <div
        style={{
          height: "67vh",
          border: "1px solid #A7A7A7",
          borderRadius: "30px",
          padding: "30px",
          boxShadow: "rgba(234, 79, 80, 0.1) 0px 4px 12px",
        }}
      >
        <Row className="h-100">
          {isReadingExercise && (<Col
            xs={6}
            style={{
              borderRight: "2px solid #8A8A8A",
              height: "52vh",
            }}
          >
            {exerciseInfo ? (
              <>
                <h3 className="text-capitalize">{t("Comprehension Passage")}</h3>
                <div
                  className="custom-scrollbar"
                  style={{
                    height: "100%",
                    overflow: "auto",
                    whiteSpace: "pre-line",
                  }}
                >
                  <p>{formatText(exerciseInfo?.passage)}</p>
                </div>
              </>
            ) : (
              <Placeholder as="div" animation="glow">
                <Placeholder xs={6} className="mb-2" />
                <Placeholder xs={8} />
                <Placeholder xs={4} />
                <Placeholder xs={7} />
              </Placeholder>
            )}
          </Col>)}
          {isListeningExercise && (
            <Col
              xs={6}
              className="custom-scrollbar"
              style={{
                height: "60vh",
                overflow: "auto",
                borderRight: "2px solid #8A8A8A",
              }}
            >
              <div className="d-flex align-items-center justify-content-between mb-3">
                {/* <h4>{exerciseInfo.title}</h4>
                  <span className={`bg-primary px-3 mx-2 rounded-4 text-white`}>
                    {exerciseInfo.status}
                  </span> */}
                <h5>
                  {t("Listening Exercise Audio")}:
                </h5>
              </div>
              <div className="">
                <audio controls src={audioUrl} className="mt-5 w-100" />
              </div>
            </Col>
          )}
          <Col xs={6} lg={isWritingExercise ? 12 : 6} style={{ height: "60vh", overflow: "auto" }} className="custom-scrollbar" >
            {exerciseInfo?.questions.map((question, index) => {
              const studentAnswer = submittedAnswers[question._id];
              const correctAnswer = question.correctAnswer;

              const isOverridable = ["multiple-choice", "true-false"].includes(
                question.type
              );
              const overrideMark = marks.find(
                (mark) => mark.questionId === question._id
              );
              // For overridable questions the colour reflects the teacher's
              // current (possibly overridden) score, not just the auto-check.
              const markedCorrect =
                question.score > 0 && overrideMark?.score === question.score;

              const backgroundColor = isOverridable
                ? markedCorrect
                  ? "#d4edda" // Green for correct
                  : "#f8d7da" // Red for incorrect
                : "transparent";

              return (
                <div
                  key={question._id}
                  style={{
                    marginBottom: "20px",
                    backgroundColor: backgroundColor,
                    padding: "10px",
                    borderRadius: "8px",
                  }}
                >
                  <h3 className="text-capitalize mb-3">
                    {`${t('Question')} ${index + 1} ${t('of')} ${exerciseInfo?.questions.length
                      }`}
                  </h3>

                  <h5 style={{ fontWeight: "700" }} className="text-capitalize">
                    {question.type.replace("-", " ")}
                  </h5>

                  <h6>{`${t('Question')}: ${question.questionText}`}</h6>

                  {/* Render for multiple-choice and true-false */}
                  {isOverridable && (
                    <>
                      <div>
                        <strong>{t("Student's Answer")}:</strong>{" "}
                        {studentAnswer || "N/A"}
                      </div>
                      <div>
                        <strong>{t("Correct Answer")}:</strong>{" "}
                        {overrideMark?.overridden
                          ? overrideMark.correctAnswer
                          : correctAnswer}
                      </div>
                      <div>
                        <strong>{t("Score")}:</strong>{" "}
                        {markedCorrect ? question.score : 0} / {question.score}
                        
                        {overrideMark?.overridden && (userRoles === "teacher" || userRoles === "admin") && (
                          <span className="badge bg-warning text-dark ms-2">
                            {t("Overridden by teacher")}
                          </span>
                        )}
                      </div>

                      {/* Allow the teacher to override the auto/AI marking. */}
                      {!isMarked && (
                        <div className="d-flex align-items-center gap-2 mt-2">
                          <strong>{t("Mark as")}:</strong>
                          <Button
                            size="sm"
                            variant={
                              markedCorrect ? "success" : "outline-success"
                            }
                            onClick={() => handleMcqMark(question, true)}
                            disabled={isMarked}
                          >
                            {t("Correct")} (+{question.score})
                          </Button>
                          <Button
                            size="sm"
                            variant={!markedCorrect ? "danger" : "outline-danger"}
                            onClick={() => handleMcqMark(question, false)}
                            disabled={isMarked}
                          >
                            {t("Incorrect")} (0)
                          </Button>
                        </div>
                      )}
                    </>
                  )}

                  {/* Render for written-answer and essay */}
                  {["written-answer", "essay", "fill-in-the-blank"].includes(question.type) && (
                    <>
                      <Form.Group>
                        <Form.Label>{t("Student's Answer")}:</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={studentAnswer || ""}
                          readOnly
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>{t("Score")} ({t("max")} {question.score}):</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          max={question.score}
                          // value={
                          //   marks.find(
                          //     (mark) => mark.questionId === question._id
                          //   )??.score || ""
                          // }
                          value={
                            marks.find((mark) => mark.questionId === question._id)?.score ?? ""
                          }
                          onChange={(e) =>
                            handleScoreChange(
                              question._id,
                              parseInt(e.target.value, 10),
                              question.score
                            )
                          }
                          disabled={isMarked} // Disable if already marked
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>{t("Feedback")}:</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={
                            marks.find(
                              (mark) => mark.questionId === question._id
                            )?.feedback || ""
                          }
                          onChange={(e) => {
                            setMarks((prevMarks) =>
                              prevMarks.map((mark) =>
                                mark.questionId === question._id
                                  ? { ...mark, feedback: e.target.value }
                                  : mark
                              )
                            );
                          }}
                          disabled={isMarked} // Disable if already marked
                        />
                      </Form.Group>
                    </>
                  )}
                </div>
              );
            })}
          </Col>
        </Row>
      </div>

      <div className="mt-2 d-flex justify-content-end align-items-center gap-2">
        {!isMarked && !loading && (
          <>
            <AutoSaveIndicator draft
              status={autoSaveStatus}
              lastSavedAt={autoSaveAt}
              isOnline={autoSaveOnline}
              className="me-auto"
            />
            <Button
              variant="primary"
              onClick={handleShowModal}
              className="mt-1 px-4 button-style py-2 border-0"
              disabled={false}
              style={{
                background: "#303972"
              }}
            >
              {t("Send Back To Revise")}
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmitMarks}
              className="mt-1 px-4 button-style py-2 border-0"
              disabled={!isFormValid}
            >
              {t("Submit Marks")}
            </Button>
          </>
        )}
      </div>
      <RevisionModal
        show={showModal}
        handleClose={handleHideModal}
        handleConfirm={handleRevision}
        loading={loading}
        feedback={feedback}
        onFeedbackChange={(e) => setFeedback(e.target.value)}
      />

      <Modal show={overrideModal.show} onHide={handleCancelOverride} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("Set Correct Answer")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            {t(
              "You are overriding the auto-marking. Enter the correct answer to display for this question."
            )}
          </p>
          <Form.Group>
            <Form.Label>{t("Correct Answer")}:</Form.Label>
            <Form.Control
              type="text"
              value={overrideModal.value}
              onChange={(e) =>
                setOverrideModal((prev) => ({ ...prev, value: e.target.value }))
              }
              autoFocus
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelOverride}>
            {t("Cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmOverride}
            disabled={!overrideModal.value.trim()}
          >
            {t("Save")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MarkSubmission;
